import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { USER_MANAGEMENT_API_CONFIG, UserManagementConfig } from '../config';
import {
  UserInvitation,
  CreateInvitationDto,
  AcceptInvitationDto,
  SendInvitationsResponse,
  GetInvitationsResponse,
  GetInvitationByTokenResponse,
  AcceptInvitationResponse,
  DeleteInvitationResponse,
  MarkRegisteredResponse,
  LinkUserResponse,
  ValidateInvitationResponse,
  PaginatedInvitationsResponse,
  InvitationFilters,
  PaginationMeta
} from '../models';

/**
 * Service for managing user invitations via the User Management API
 * 
 * This service provides comprehensive methods for:
 * - Sending email invitations to users
 * - Accepting invitations (public endpoint)
 * - Validating invitation tokens
 * - Managing pending invitations
 * 
 * @remarks
 * **Authentication:** Most endpoints require Admin authentication.
 * Public endpoints: accept invitation, validate token, get by token.
 * 
 * **Invitation Flow:**
 * 1. Admin sends invitations → Emails sent with unique tokens
 * 2. User receives email and clicks accept link
 * 3. User accepts invitation → Marks as accepted
 * 4. User registers or links existing account → Joins organization
 * 
 * **Expiration:**
 * - Invitations expire after 7 days
 * - Expired invitations are automatically deleted by TTL index
 * 
 * @example
 * ```typescript
 * constructor(private invitationService: UserInvitationService) {}
 * 
 * // Send invitations (Admin only)
 * this.invitationService.sendInvitations([
 *   {
 *     email: 'user@example.com',
 *     name: 'John Doe',
 *     orgCode: 'MY-ORG',
 *     role: 'Editor'
 *   }
 * ]).subscribe(response => {
 *   console.log('Invitations sent:', response.invitations);
 * });
 * 
 * // Accept invitation (Public - no auth required)
 * this.invitationService.acceptInvitation('invitation-token')
 *   .subscribe(response => {
 *     console.log('Invitation accepted:', response.message);
 *   });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class UserInvitationService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(USER_MANAGEMENT_API_CONFIG);
  private readonly apiUrl: string;

  constructor() {
    this.apiUrl = `${this.config.apiBaseUrl}/api/invitations`;
    this.logDebug('UserInvitationService initialized', { apiUrl: this.apiUrl });
  }

  // ============================================================================
  // ADMIN ENDPOINTS (Authentication Required)
  // ============================================================================

  /**
   * Send invitations to users
   * 
   * @param invitations - Array of invitation data
   * @returns Observable of response with created invitations
   * 
   * @throws {Error} 400 - Invalid request data
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden (Admin role required)
   * 
   * @remarks
   * **Admin Only** - Requires Admin role.
   * 
   * Creates invitation records and sends emails to invitees.
   * Each invitation gets a unique 16-character token.
   * Invitations expire after 7 days.
   * 
   * @example
   * ```typescript
   * this.invitationService.sendInvitations([
   *   {
   *     email: 'user1@example.com',
   *     name: 'User One',
   *     orgCode: 'TECH-CO',
   *     role: 'Editor'
   *   },
   *   {
   *     email: 'user2@example.com',
   *     name: 'User Two',
   *     orgCode: 'TECH-CO',
   *     role: 'Reader'
   *   }
   * ]).subscribe({
   *   next: response => {
   *     console.log(`${response.invitations.length} invitations sent`);
   *     response.invitations.forEach(inv => {
   *       console.log(`${inv.email} - Token: ${inv.token}`);
   *     });
   *   },
   *   error: err => console.error('Failed to send invitations:', err)
   * });
   * ```
   */
  sendInvitations(invitations: CreateInvitationDto[]): Observable<SendInvitationsResponse> {
    this.logDebug('Sending invitations', { count: invitations.length });

    return this.http.post<UserInvitation[]>(this.apiUrl, invitations).pipe(
      map((invitations) => {
        this.logDebug('Invitations sent successfully', { count: invitations.length });
        return { 
          invitations,
          message: `${invitations.length} invitation(s) sent successfully`
        };
      }),
      catchError(error => this.handleError('sendInvitations', error))
    );
  }

  /**
   * Get invitations with advanced filtering and pagination
   * 
   * @param filters - Filter criteria including orgId (required), pagination, and search parameters
   * @returns Observable of paginated response with invitations and pagination metadata
   * 
   * @throws {Error} 400 - Missing required orgId parameter or invalid filters
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden (User doesn't have access to organization)
   * 
   * @remarks
   * **Authentication Required** - User must have access to the specified organization.
   * 
   * This is the primary method for retrieving invitations with full filtering support.
   * Supports pagination, status filtering, and text search.
   * 
   * **Query Parameters:**
   * - `orgId` (required): Organization ID (MongoDB ObjectId)
   * - `page` (optional): Page number (default: 1)
   * - `limit` (optional): Items per page (default: 10, max: 100)
   * - `expired` (optional): Filter by expiration status
   * - `accepted` (optional): Filter by acceptance status
   * - `registered` (optional): Filter by registration status
   * - `userName` (optional): Search by user name (partial match)
   * - `email` (optional): Search by email (partial match)
   * 
   * @example
   * ```typescript
   * // Get first page of pending invitations for an organization
   * this.invitationService.getInvitations({
   *   orgId: '64abc123def456789012',
   *   page: 1,
   *   limit: 20,
   *   expired: false,
   *   accepted: false
   * }).subscribe({
   *   next: response => {
   *     console.log('Invitations:', response.data.invitations);
   *     console.log('Total pages:', response.data.pagination.pages);
   *     console.log('Total invitations:', response.data.pagination.total);
   *   },
   *   error: err => console.error('Failed to load invitations:', err)
   * });
   * 
   * // Search invitations by email
   * this.invitationService.getInvitations({
   *   orgId: '64abc123def456789012',
   *   email: 'john@example.com'
   * }).subscribe(response => {
   *   console.log('Found:', response.data.invitations);
   * });
   * ```
   */
  getInvitations(filters: InvitationFilters): Observable<PaginatedInvitationsResponse> {
    this.logDebug('Getting invitations with filters', { filters });

    // Validate required orgId parameter
    if (!filters.orgId) {
      const error = new Error('orgId is required');
      this.logDebug('getInvitations failed: missing orgId');
      return throwError(() => ({
        operation: 'getInvitations',
        message: 'orgId is required',
        status: 400,
        originalError: error
      }));
    }

    // Build query parameters
    let params = new HttpParams();
    Object.keys(filters).forEach(key => {
      const value = filters[key as keyof InvitationFilters];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return this.http.get<PaginatedInvitationsResponse>(this.apiUrl, { params }).pipe(
      map((response) => {
        this.logDebug('Invitations retrieved with pagination', {
          count: response.data.invitations.length,
          total: response.data.pagination.total,
          page: response.data.pagination.page,
          pages: response.data.pagination.pages
        });
        return response;
      }),
      catchError(error => this.handleError('getInvitations', error))
    );
  }

  /**
   * Get all pending invitations (Legacy method - maintained for backward compatibility)
   * 
   * @param orgId - Organization ID (MongoDB ObjectId) - REQUIRED for API v2
   * @returns Observable of response with invitations array
   * 
   * @throws {Error} 400 - Missing required orgId parameter
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden (User doesn't have access to organization)
   * 
   * @remarks
   * **Authentication Required** - User must have access to the specified organization.
   * 
   * **IMPORTANT:** This method signature has changed to require orgId parameter
   * due to backend API updates. Update all calling code to provide orgId.
   * 
   * @deprecated Consider using `getInvitations()` or `getPendingInvitations()` 
   * for better control over filtering and pagination.
   * 
   * Returns only non-expired and unaccepted invitations.
   * Uses default pagination (page 1, limit 10).
   * 
   * @example
   * ```typescript
   * // Old usage (no longer supported):
   * // this.invitationService.getAllInvitations().subscribe(...)
   * 
   * // New usage (required):
   * this.invitationService.getAllInvitations('64abc123def456789012')
   *   .subscribe({
   *     next: response => {
   *       console.log('Pending invitations:', response.invitations);
   *     },
   *     error: err => console.error('Failed to load invitations:', err)
   *   });
   * 
   * // Recommended alternative:
   * this.invitationService.getPendingInvitations('64abc123def456789012')
   *   .subscribe(response => {
   *     console.log('Invitations:', response.data.invitations);
   *     console.log('Pagination:', response.data.pagination);
   *   });
   * ```
   */
  getAllInvitations(orgId: string): Observable<GetInvitationsResponse> {
    this.logDebug('Getting all invitations (legacy method)', { orgId });

    if (!orgId) {
      const error = new Error('orgId is required');
      this.logDebug('getAllInvitations failed: missing orgId');
      return throwError(() => ({
        operation: 'getAllInvitations',
        message: 'orgId parameter is required. Backend API now requires organization ID.',
        status: 400,
        originalError: error
      }));
    }

    // Use the new API with default filters for pending invitations
    return this.getInvitations({
      orgId,
      page: 1,
      limit: 100, // Return more items for legacy compatibility
      expired: false,
      accepted: false
    }).pipe(
      map((response) => {
        this.logDebug('Invitations retrieved (legacy format)', { 
          count: response.data.invitations.length 
        });
        // Convert to legacy format
        return { invitations: response.data.invitations };
      }),
      catchError(error => this.handleError('getAllInvitations', error))
    );
  }

  /**
   * Delete an invitation
   * 
   * @param invitationId - Invitation ID to delete
   * @returns Observable of response confirming deletion
   * 
   * @throws {Error} 400 - Invalid invitation ID
   * @throws {Error} 401 - Unauthorized
   * @throws {Error} 403 - Forbidden (Admin role required)
   * @throws {Error} 404 - Invitation not found
   * 
   * @remarks
   * **Admin Only** - Requires Admin role.
   * 
   * Permanently deletes the invitation.
   * Use this to revoke an invitation before it's accepted.
   * 
   * @example
   * ```typescript
   * this.invitationService.deleteInvitation('507f1f77bcf86cd799439011')
   *   .subscribe({
   *     next: response => console.log(response.message),
   *     error: err => console.error('Delete failed:', err)
   *   });
   * ```
   */
  deleteInvitation(invitationId: string): Observable<DeleteInvitationResponse> {
    this.logDebug('Deleting invitation', { invitationId });

    return this.http.delete<void>(`${this.apiUrl}/${invitationId}`).pipe(
      map(() => {
        this.logDebug('Invitation deleted successfully', { invitationId });
        return { message: 'Invitation deleted successfully' as const };
      }),
      catchError(error => this.handleError('deleteInvitation', error))
    );
  }

  // ============================================================================
  // PUBLIC ENDPOINTS (No Authentication Required)
  // ============================================================================

  /**
   * Get invitation by token (Public endpoint)
   * 
   * @param token - Invitation token
   * @returns Observable of response with invitation data
   * 
   * @throws {Error} 404 - Invitation not found or expired
   * 
   * @remarks
   * **Public Endpoint** - No authentication required.
   * 
   * Use this to retrieve invitation details when user clicks email link.
   * Returns invitation info if token is valid and not expired.
   * 
   * @example
   * ```typescript
   * this.invitationService.getInvitationByToken('abc123def456')
   *   .subscribe({
   *     next: response => {
   *       const inv = response.invitation;
   *       console.log(`You're invited to join ${inv.orgCode} as ${inv.role}`);
   *     },
   *     error: err => {
   *       console.error('Invalid or expired invitation');
   *     }
   *   });
   * ```
   */
  getInvitationByToken(token: string): Observable<GetInvitationByTokenResponse> {
    this.logDebug('Getting invitation by token', { token });

    return this.http.get<UserInvitation>(`${this.apiUrl}/${token}`).pipe(
      map((invitation) => {
        this.logDebug('Invitation retrieved', { email: invitation.email });
        return { invitation };
      }),
      catchError(error => this.handleError('getInvitationByToken', error))
    );
  }

  /**
   * Accept an invitation (Public endpoint)
   * 
   * @param token - Invitation token
   * @param dto - Optional data (e.g., updated name)
   * @returns Observable of response with updated invitation
   * 
   * @throws {Error} 400 - Invalid request
   * @throws {Error} 404 - Invitation not found or expired
   * 
   * @remarks
   * **Public Endpoint** - No authentication required.
   * 
   * Marks invitation as accepted.
   * User still needs to register or link their account to join the organization.
   * 
   * @example
   * ```typescript
   * this.invitationService.acceptInvitation('abc123def456')
   *   .subscribe({
   *     next: response => {
   *       console.log(response.message);
   *       console.log('Accepted at:', response.invitation.acceptedAt);
   *     },
   *     error: err => {
   *       console.error('Failed to accept invitation:', err);
   *     }
   *   });
   * ```
   */
  acceptInvitation(token: string, dto?: AcceptInvitationDto): Observable<AcceptInvitationResponse> {
    this.logDebug('Accepting invitation', { token });

    return this.http.put<AcceptInvitationResponse>(`${this.apiUrl}/${token}/accept`, dto || {}).pipe(
      map((response) => {
        this.logDebug('Invitation accepted', { email: response.invitation.email });
        return response;
      }),
      catchError(error => this.handleError('acceptInvitation', error))
    );
  }

  /**
   * Mark an invitation as registered after user completes registration
   *
   * @param token - Invitation token
   * @returns Observable of response confirming registration status
   *
   * @throws {Error} 401 - Unauthorized (authentication required)
   * @throws {Error} 404 - Invitation not found or expired
   *
   * @remarks
   * **Authentication Required** — call this after a new user finishes the
   * registration flow to flag the invitation as fully onboarded.
   * This is distinct from `acceptInvitation()` which only marks the first
   * click-through on the invite link.
   *
   * @example
   * ```typescript
   * this.invitationService.markAsRegistered('abc123def456')
   *   .subscribe({
   *     next: response => {
   *       console.log(response.message);
   *       console.log('Registered at:', response.invitation.registeredAt);
   *     },
   *     error: err => console.error('Failed to mark as registered:', err)
   *   });
   * ```
   */
  markAsRegistered(token: string): Observable<MarkRegisteredResponse> {
    this.logDebug('Marking invitation as registered', { token });

    return this.http.post<MarkRegisteredResponse>(`${this.apiUrl}/${token}/register`, {}).pipe(
      map((response) => {
        this.logDebug('Invitation marked as registered', { token });
        return response;
      }),
      catchError(error => this.handleError('markAsRegistered', error))
    );
  }

  /**
   * Link an existing authenticated user to an organization via invitation token
   *
   * @param token - Invitation token
   * @returns Observable of response with organization and role details
   *
   * @throws {Error} 400 - Invalid token or invitation already used
   * @throws {Error} 401 - Unauthorized (authentication required)
   * @throws {Error} 403 - Forbidden (authenticated user's email does not match invitation email)
   * @throws {Error} 404 - User or organization not found
   *
   * @remarks
   * **Authentication Required** — unlike `acceptInvitation()`, this endpoint
   * requires the user to be signed in. It is used by users who **already have
   * an account** and are joining a new organization via an invite link.
   *
   * The backend validates that the authenticated user's email matches the
   * invitation email before linking. On success the invitation's `registered`
   * flag is set to `true` and the user is added to the organization with the
   * role specified in the invitation.
   *
   * **Typical flow for existing users:**
   * 1. User clicks invite link → `getInvitationByToken()`
   * 2. User signs in (already has account)
   * 3. Call `linkUserToOrganization()` — completes onboarding in one step
   *
   * @example
   * ```typescript
   * this.invitationService.linkUserToOrganization('abc123def456')
   *   .subscribe({
   *     next: response => {
   *       console.log(response.message);
   *       console.log('Joined org:', response.data.organization.orgCode);
   *       console.log('Role:', response.data.userRole);
   *     },
   *     error: err => console.error('Failed to link user:', err)
   *   });
   * ```
   */
  linkUserToOrganization(token: string): Observable<LinkUserResponse> {
    this.logDebug('Linking user to organization via invitation', { token });

    return this.http.put<LinkUserResponse>(`${this.apiUrl}/${token}/link-user`, {}).pipe(
      map((response) => {
        this.logDebug('User linked to organization', {
          orgCode: response.data.organization.orgCode,
          role: response.data.userRole
        });
        return response;
      }),
      catchError(error => this.handleError('linkUserToOrganization', error))
    );
  }

  // ============================================================================
  // CONVENIENCE METHODS
  // ============================================================================

  /**
   * Get pending invitations (non-expired and unaccepted)
   * 
   * @param orgId - Organization ID (MongoDB ObjectId)
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10, max: 100)
   * @returns Observable of paginated invitations response
   * 
   * @remarks
   * Helper method to get active pending invitations.
   * Filters out expired and already accepted invitations.
   * 
   * @example
   * ```typescript
   * this.invitationService.getPendingInvitations(
   *   '64abc123def456789012',
   *   1,
   *   20
   * ).subscribe(response => {
   *   console.log('Pending invitations:', response.data.invitations);
   *   console.log(`Page ${response.data.pagination.page} of ${response.data.pagination.pages}`);
   * });
   * ```
   */
  getPendingInvitations(
    orgId: string,
    page: number = 1,
    limit: number = 10
  ): Observable<PaginatedInvitationsResponse> {
    this.logDebug('Getting pending invitations', { orgId, page, limit });
    return this.getInvitations({
      orgId,
      page,
      limit,
      expired: false,
      accepted: false
    });
  }

  /**
   * Get expired invitations
   * 
   * @param orgId - Organization ID (MongoDB ObjectId)
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10, max: 100)
   * @returns Observable of paginated invitations response
   * 
   * @remarks
   * Helper method to retrieve expired invitations.
   * Useful for cleanup and reporting purposes.
   * 
   * @example
   * ```typescript
   * this.invitationService.getExpiredInvitations(
   *   '64abc123def456789012',
   *   1,
   *   50
   * ).subscribe(response => {
   *   console.log('Expired invitations:', response.data.invitations);
   * });
   * ```
   */
  getExpiredInvitations(
    orgId: string,
    page: number = 1,
    limit: number = 10
  ): Observable<PaginatedInvitationsResponse> {
    this.logDebug('Getting expired invitations', { orgId, page, limit });
    return this.getInvitations({
      orgId,
      page,
      limit,
      expired: true
    });
  }

  /**
   * Get accepted invitations
   * 
   * @param orgId - Organization ID (MongoDB ObjectId)
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10, max: 100)
   * @returns Observable of paginated invitations response
   * 
   * @remarks
   * Helper method to retrieve accepted invitations.
   * Shows invitations that users have accepted but may not have completed registration.
   * 
   * @example
   * ```typescript
   * this.invitationService.getAcceptedInvitations(
   *   '64abc123def456789012'
   * ).subscribe(response => {
   *   console.log('Accepted invitations:', response.data.invitations);
   *   response.data.invitations.forEach(inv => {
   *     console.log(`${inv.name} accepted on ${inv.acceptedAt}`);
   *   });
   * });
   * ```
   */
  getAcceptedInvitations(
    orgId: string,
    page: number = 1,
    limit: number = 10
  ): Observable<PaginatedInvitationsResponse> {
    this.logDebug('Getting accepted invitations', { orgId, page, limit });
    return this.getInvitations({
      orgId,
      page,
      limit,
      accepted: true
    });
  }

  /**
   * Get registered invitations (users who completed registration)
   * 
   * @param orgId - Organization ID (MongoDB ObjectId)
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10, max: 100)
   * @returns Observable of paginated invitations response
   * 
   * @remarks
   * Helper method to retrieve invitations where users completed registration.
   * These users have successfully joined the organization.
   * 
   * @example
   * ```typescript
   * this.invitationService.getRegisteredInvitations(
   *   '64abc123def456789012'
   * ).subscribe(response => {
   *   console.log('Registered users:', response.data.invitations);
   * });
   * ```
   */
  getRegisteredInvitations(
    orgId: string,
    page: number = 1,
    limit: number = 10
  ): Observable<PaginatedInvitationsResponse> {
    this.logDebug('Getting registered invitations', { orgId, page, limit });
    return this.getInvitations({
      orgId,
      page,
      limit,
      registered: true
    });
  }

  /**
   * Search invitations by user name or email
   * 
   * @param orgId - Organization ID (MongoDB ObjectId)
   * @param searchTerm - Search term for name or email (case-insensitive, partial match)
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10, max: 100)
   * @returns Observable of paginated invitations response
   * 
   * @remarks
   * Helper method for searching invitations by user name or email.
   * Performs case-insensitive partial matching on both fields.
   * 
   * @example
   * ```typescript
   * this.invitationService.searchInvitations(
   *   '64abc123def456789012',
   *   'john',
   *   1,
   *   20
   * ).subscribe(response => {
   *   console.log('Search results:', response.data.invitations);
   * });
   * ```
   */
  searchInvitations(
    orgId: string,
    searchTerm: string,
    page: number = 1,
    limit: number = 10
  ): Observable<PaginatedInvitationsResponse> {
    this.logDebug('Searching invitations', { orgId, searchTerm, page, limit });
    return this.getInvitations({
      orgId,
      page,
      limit,
      userName: searchTerm,
      email: searchTerm
    });
  }

  /**
   * Validate invitation token
   * 
   * @param token - Invitation token to validate
   * @returns Observable of validation result
   * 
   * @remarks
   * Checks if token is valid and not expired.
   * Returns validation status and invitation data if valid.
   * 
   * @example
   * ```typescript
   * this.invitationService.validateToken('abc123def456')
   *   .subscribe(result => {
   *     if (result.valid) {
   *       console.log('Valid invitation:', result.invitation);
   *     } else {
   *       console.log('Invalid:', result.message);
   *     }
   *   });
   * ```
   */
  validateToken(token: string): Observable<ValidateInvitationResponse> {
    return this.getInvitationByToken(token).pipe(
      map(response => ({
        valid: true,
        invitation: response.invitation
      })),
      catchError(() => {
        return [{
          valid: false,
          message: 'Invitation not found or has expired'
        }];
      })
    );
  }

  /**
   * Send single invitation
   * 
   * @param invitation - Single invitation data
   * @returns Observable of created invitation
   * 
   * @remarks
   * Convenience wrapper for sending a single invitation.
   * 
   * @example
   * ```typescript
   * this.invitationService.sendInvitation({
   *   email: 'user@example.com',
   *   name: 'John Doe',
   *   orgCode: 'TECH-CO',
   *   role: 'Editor'
   * }).subscribe(invitation => {
   *   console.log('Invitation sent:', invitation.token);
   * });
   * ```
   */
  sendInvitation(invitation: CreateInvitationDto): Observable<UserInvitation> {
    return this.sendInvitations([invitation]).pipe(
      map(response => response.invitations[0])
    );
  }

  /**
   * Get invitations data without wrapper (Legacy helper)
   * 
   * @param orgId - Organization ID (MongoDB ObjectId) - REQUIRED
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 100 for legacy compatibility)
   * @returns Observable of invitations array
   * 
   * @remarks
   * **UPDATED:** Now requires orgId parameter due to backend API changes.
   * Returns invitations array without pagination wrapper for backward compatibility.
   * 
   * @example
   * ```typescript
   * this.invitationService.getInvitationsData('64abc123def456789012')
   *   .subscribe(invitations => {
   *     console.log('Invitations:', invitations);
   *   });
   * ```
   */
  getInvitationsData(
    orgId: string,
    page: number = 1,
    limit: number = 100
  ): Observable<UserInvitation[]> {
    if (!orgId) {
      return throwError(() => ({
        operation: 'getInvitationsData',
        message: 'orgId parameter is required',
        status: 400
      }));
    }

    return this.getInvitations({
      orgId,
      page,
      limit,
      expired: false,
      accepted: false
    }).pipe(
      map(response => response.data.invitations)
    );
  }

  /**
   * Get pending invitations count
   * 
   * @param orgId - Organization ID (MongoDB ObjectId) - REQUIRED
   * @returns Observable of count
   * 
   * @remarks
   * **UPDATED:** Now requires orgId parameter due to backend API changes.
   * Returns total count of pending invitations from pagination metadata.
   * 
   * @example
   * ```typescript
   * this.invitationService.getInvitationsCount('64abc123def456789012')
   *   .subscribe(count => {
   *     console.log(`Total pending invitations: ${count}`);
   *   });
   * ```
   */
  getInvitationsCount(orgId: string): Observable<number> {
    if (!orgId) {
      return throwError(() => ({
        operation: 'getInvitationsCount',
        message: 'orgId parameter is required',
        status: 400
      }));
    }

    return this.getInvitations({
      orgId,
      page: 1,
      limit: 1, // We only need the count, not the data
      expired: false,
      accepted: false
    }).pipe(
      map(response => response.data.pagination.total)
    );
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Handle HTTP errors
   */
  private handleError(operation: string, error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client error: ${error.error.message}`;
    } else {
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.status) {
        errorMessage = `Server error: ${error.status} - ${error.statusText}`;
      }
    }

    this.logDebug(`${operation} failed`, { error: errorMessage, status: error.status });

    return throwError(() => ({
      operation,
      message: errorMessage,
      status: error.status,
      originalError: error
    }));
  }

  /**
   * Log debug information
   */
  private logDebug(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[UserInvitationService] ${message}`, data || '');
    }
  }
}
